'use client'

import {
  Block24,
  CancelCircle24,
  Flag24,
  QuestionCircle24,
  TrashBin24,
} from '@vinted/monochrome-icons'
import { Cell, Icon, Note } from '@vinted/web-ui'
import { useMemo, useState } from 'react'
import { v4 as uuid } from 'uuid'

import {
  DropOffPointModal,
  CancelTransactionModal,
} from '@marketplace-web/conversation-shipping/conversation-shipping-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  navigateToPage,
  reloadPage,
  setSessionStorageItem,
  useLocation,
} from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { urlWithParams } from '@marketplace-web/shared/utils'
import { useBrazeInboxMessageCards } from '@marketplace-web/braze/braze-feature'
import {
  OppositeUser,
  TransactionModel,
  AdditionalTransactionAction,
  TransactionSide,
  INBOX_URL,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { REPORT_SESSION_ID_KEY } from '@marketplace-web/domain/admin-alerts-data'

import { clickEvent } from '_libs/common/event-tracker/events'
import UserBlockModal from 'components/UserBlockModal'
import { AccessChannel } from 'constants/access-channel'
import { AlertType } from 'constants/admin'
import {
  HELP_FAQ_ENTRIES_URL,
  NEW_ADMIN_ALERT_URL,
  TRANSACTION_FAQ_ENTRY_URL,
} from 'constants/routes'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { blockUser } from 'data/api/block-user'
import { unblockUser } from 'data/api/unblock-user'

import DeleteConversationModal from '../DeleteConversationModal'

enum ConversationActionModal {
  CancelTransaction,
  DeleteConversation,
  BlockUser,
  DropOffPoint,
  CarrierChange,
}

type Props = {
  transaction?: TransactionModel | null
  conversationId?: number | string
  oppositeUser?: OppositeUser | null
  userId?: number
}

const ConversationActions = ({ transaction, conversationId, oppositeUser, userId }: Props) => {
  const baseTranslate = useTranslate()
  const translate = useTranslate('conversation.conversation_info')
  const { track } = useTracking()
  const { relativeUrl } = useLocation()
  const { conversation } = useConversationContext()

  const isBlockingImprovementsEnabled = useFeatureSwitch('conversation_blocking_improvements')
  const isConversationDeletionRestricted = conversation?.isDeletionRestricted

  const userSide = conversation?.transaction?.userSide
  const isBuyer = userSide === TransactionSide.Buyer
  const hideOrderDetailsAction = conversation?.orderDetailsVariant === 'c'

  const [visibleModalName, setVisibleModalName] = useState<ConversationActionModal | null>(null)

  const { inboxMessageCards } = useBrazeInboxMessageCards()
  const brazeConversationCard = useMemo(
    () => inboxMessageCards?.find(card => card.id === conversationId),
    [conversationId, inboxMessageCards],
  )

  function closeModals() {
    setVisibleModalName(null)
  }

  function handleCancelTransactionClick() {
    setVisibleModalName(ConversationActionModal.CancelTransaction)
  }

  function handleCancelTransactionSuccess() {
    closeModals()

    if (!conversationId || !userId) return

    navigateToPage(INBOX_URL)
  }

  function handleDeleteConversationClick() {
    setVisibleModalName(ConversationActionModal.DeleteConversation)
  }

  function handleDeleteConversation() {
    closeModals()

    navigateToPage(INBOX_URL)
  }

  function handleModalUserBlock() {
    closeModals()
    reloadPage()
  }

  function handleDropOffPointModalOpen() {
    setVisibleModalName(ConversationActionModal.DropOffPoint)
  }

  function handleDropOffPointModalClose() {
    setVisibleModalName(ConversationActionModal.CancelTransaction)
  }

  async function handleBlockUserClick() {
    if (!oppositeUser || !userId) return

    if (isBlockingImprovementsEnabled) {
      setVisibleModalName(ConversationActionModal.BlockUser)

      return
    }

    const blockFunction = oppositeUser.isHated ? unblockUser : blockUser

    await blockFunction({ currentUserId: userId, blockedUserId: Number(oppositeUser.id) })

    reloadPage()
  }

  function trackHelp() {
    if (!transaction) return

    const targetDetails = {
      transaction_id: transaction.id,
    }

    track(
      clickEvent({
        target: ClickableElement.Help,
        screen: Screen.OrderDetails,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function handleReportClick() {
    setSessionStorageItem(REPORT_SESSION_ID_KEY, uuid())

    track(
      clickEvent({
        screen: Screen.ConversationList,
        target: ClickableElement.ReportConversation,
        targetDetails: String(conversationId),
      }),
    )
  }

  function getHelpCenterUrl() {
    if (!transaction) {
      return urlWithParams(HELP_FAQ_ENTRIES_URL, {
        access_channel: AccessChannel.ConversationNoTx,
        ref_url: relativeUrl,
      })
    }

    return urlWithParams(TRANSACTION_FAQ_ENTRY_URL(transaction.id), {
      access_channel: AccessChannel.ConversationTx,
      ref_url: relativeUrl,
      related_only: false,
    })
  }

  function renderCancelTransactionModal() {
    if (!transaction) return null

    return (
      <CancelTransactionModal
        show={visibleModalName === ConversationActionModal.CancelTransaction}
        transactionId={transaction.id}
        isBuyer={isBuyer}
        onSuccess={handleCancelTransactionSuccess}
        onCancel={closeModals}
        onDropOffPointModalOpen={handleDropOffPointModalOpen}
      />
    )
  }

  function renderDropOffPointModal() {
    if (!transaction) return null

    return (
      <DropOffPointModal
        isOpen={visibleModalName === ConversationActionModal.DropOffPoint}
        onClose={handleDropOffPointModalClose}
      />
    )
  }

  function renderHelpAction() {
    return (
      <Cell
        title={translate('conversation_actions.help')}
        prefix={<Icon name={QuestionCircle24} color={Icon.Color.Primary} />}
        type={Cell.Type.Navigating}
        url={getHelpCenterUrl()}
        onClick={trackHelp}
      />
    )
  }

  function renderCancelTransactionAction() {
    if (hideOrderDetailsAction) return null

    const isCancelActionExist = transaction?.availableActions.includes(
      AdditionalTransactionAction.Cancel,
    )

    if (!isCancelActionExist || !transaction) return null

    return (
      <>
        <Cell
          title={translate('conversation_actions.cancel')}
          prefix={<Icon name={CancelCircle24} color={Icon.Color.Warning} />}
          type={Cell.Type.Navigating}
          onClick={handleCancelTransactionClick}
          testId="conversation-actions-cancel"
        />
        {renderCancelTransactionModal()}
        {renderDropOffPointModal()}
      </>
    )
  }

  function renderMemberReportAction() {
    const isDeleteConversationActionExist = transaction?.availableActions?.includes(
      AdditionalTransactionAction.DeleteConversation,
    )
    const isReportingRestrictedForUser = transaction && !isDeleteConversationActionExist

    if (
      isReportingRestrictedForUser ||
      !conversationId ||
      !oppositeUser ||
      oppositeUser.isSystem ||
      !!brazeConversationCard
    )
      return null

    const reportUrl = urlWithParams(NEW_ADMIN_ALERT_URL, {
      ref_id: conversationId,
      ref_type: AlertType.Conversation,
      ref_url: relativeUrl,
    })

    return (
      <Cell
        title={translate('conversation_actions.report')}
        prefix={<Icon name={Flag24} color={Icon.Color.Warning} />}
        type={Cell.Type.Navigating}
        url={reportUrl}
        onClick={handleReportClick}
        testId="conversation-actions-report"
      />
    )
  }

  function renderDeleteConversationAction() {
    const isDeleteConversationActionExist = transaction?.availableActions?.includes(
      AdditionalTransactionAction.DeleteConversation,
    )

    if ((transaction && !isDeleteConversationActionExist) || !conversationId || !userId) return null

    return (
      <>
        <Cell
          title={translate('conversation_actions.delete')}
          prefix={<Icon name={TrashBin24} color={Icon.Color.Warning} />}
          type={Cell.Type.Navigating}
          onClick={handleDeleteConversationClick}
          testId="conversation-actions-delete"
          disabled={isConversationDeletionRestricted}
        />
        {isConversationDeletionRestricted && (
          <Note text={translate('conversation_actions.appeal_delete_note')} />
        )}
        <DeleteConversationModal
          conversationId={conversationId}
          show={visibleModalName === ConversationActionModal.DeleteConversation}
          onConfirm={handleDeleteConversation}
          onCancel={closeModals}
        />
      </>
    )
  }

  function renderBlockAction() {
    if (!oppositeUser || oppositeUser.isUserUnblockable || !!brazeConversationCard) return null

    const actionKey = oppositeUser.isHated ? 'unblock' : 'block'

    return (
      <>
        <Cell
          title={baseTranslate(`user.admin.actions.${actionKey}`)}
          prefix={<Icon name={Block24} color={Icon.Color.Warning} />}
          type={Cell.Type.Navigating}
          onClick={handleBlockUserClick}
          testId={`conversation-actions-${actionKey}`}
        />
        <UserBlockModal
          userId={Number(oppositeUser.id)}
          username={oppositeUser.login}
          isUserBlocked={oppositeUser.isHated}
          isVisible={visibleModalName === ConversationActionModal.BlockUser}
          onCancel={closeModals}
          onConfirm={handleModalUserBlock}
        />
      </>
    )
  }

  return (
    <>
      {renderHelpAction()}
      {renderCancelTransactionAction()}
      {renderMemberReportAction()}
      {renderDeleteConversationAction()}
      {renderBlockAction()}
    </>
  )
}

export default ConversationActions
